import { Link } from 'react-router-dom'
import { Button } from '@gjl/ui'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useDirectionalReveal, useSectionSequence, useAlternatingReveal, useLuxeGrid } from '@/hooks'
import {
  ArrowRight,
  Code2,
  Cloud,
  Database,
  Cpu,
  GitBranch,
  Zap,
  Terminal,
  Briefcase,
  GraduationCap,
  Award,
} from 'lucide-react'

const skills = [
  { icon: Cpu, name: 'AWS Bedrock (Claude)', category: 'AI/LLM' },
  { icon: Terminal, name: 'Multi-agent orchestration', category: 'AI Systems' },
  { icon: Terminal, name: 'LLM grounding & safety gates', category: 'AI Systems' },
  { icon: Terminal, name: 'Context engineering / RAG', category: 'AI Systems' },
  { icon: Cloud, name: 'Terraform / IaC', category: 'Infrastructure' },
  { icon: Cpu, name: 'AWS Lambda / Serverless', category: 'Cloud' },
  { icon: Code2, name: 'TypeScript / React', category: 'Languages' },
  { icon: Code2, name: 'Python', category: 'Languages' },
  { icon: Code2, name: 'C# / .NET', category: 'Languages' },
  { icon: Zap, name: 'Observability / CloudWatch', category: 'Monitoring' },
  { icon: GitBranch, name: 'CI/CD Pipelines', category: 'DevOps' },
  { icon: Database, name: 'DynamoDB / PostgreSQL', category: 'Data' },
]

const experience = [
  {
    title: 'AI Chatbot Infrastructure (from scratch)',
    description:
      'Built the AI chatbot platform’s infrastructure end to end — Terraform across dev/staging/prod, observability, and CI/CD — supporting a RAG-backed assistant in production.',
  },
  {
    title: 'RAG Pipeline Optimization',
    description:
      'Replaced a slow Bedrock embedding path with LiteLLM + batched embeddings for a 20x speedup on the RAG pipeline.',
  },
  {
    title: 'Build vs Buy → 6 to 2 engineers',
    description:
      'Made the build-vs-buy call to adopt Open WebUI over a custom frontend, cutting the team from 6 engineers to 2 and shipping in weeks instead of months.',
  },
  {
    title: 'Observability Stack',
    description:
      'Built the observability stack so product can see usage and engineering can watch performance, with alarms on critical regressions.',
  },
  {
    title: 'Backend APIs',
    description: 'Designed backend APIs for CNC quoting workflows using .NET, GraphQL, and DynamoDB.',
  },
  {
    title: 'Manufacturing Scheduler',
    description:
      'Delivered a finite-capacity scheduler in 1 month through daily customer meetings — beat a vendor solution priced at $30k/year.',
  },
]

const education = [
  {
    title: 'Certificate in Real Estate Investment & Finance',
    institution: 'UCLA Extension',
    status: 'In Progress',
    icon: Award,
  },
  {
    title: 'Coding Bootcamp',
    institution: 'Tech Elevator',
    status: '2021',
    icon: Terminal,
  },
  {
    title: 'Master of Music',
    institution: 'Cleveland Institute of Music',
    status: '2018',
    icon: GraduationCap,
  },
  {
    title: 'Bachelor of Music',
    institution: 'Cleveland Institute of Music',
    status: '2017',
    icon: GraduationCap,
  },
]

export function Resume() {
  // Hero: premium entrance
  const heroRef = useLuxeHero<HTMLElement>(0.3)

  // Skills: section sequence
  const skillsRef = useSectionSequence<HTMLElement>([
    { selector: 'h2', direction: 'left', delay: 0 },
    { selector: '.skill-card', direction: 'bottom', stagger: 0.06, delay: 0.1 },
  ])

  // Experience: slide from right
  const experienceRef = useDirectionalReveal<HTMLElement>('.reveal', 'right', {
    stagger: 0.12,
  })

  // Projects: alternating
  const projectsRef = useAlternatingReveal<HTMLElement>('.project-card', {
    offset: 50,
    blur: 4,
  })

  // Education: grid stagger
  const educationRef = useLuxeGrid<HTMLElement>('.edu-card', {
    stagger: 0.1,
  })

  // Contact: scale reveal
  const contactRef = useDirectionalReveal<HTMLElement>('.reveal', 'fade', {
    scale: 0.95,
    blur: 6,
  })

  return (
    <>
      <SEO
        title="Résumé"
        description="AI engineer — production LLM systems. Retrieval quality, agent orchestration, eval harnesses, LLM observability, inference cost, and guardrails. AWS Bedrock, Terraform, Python, TypeScript."
        url="https://software.garrettjohnlaw.com/resume"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-20 md:py-28">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <p className="text-primary mb-4 font-mono text-lg font-medium tracking-wide">&lt;résumé /&gt;</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Garrett John Law</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto mb-8 max-w-2xl">
            AI Engineer, Production LLM Systems · Retrieval &amp; agents · Evals, observability &amp; cost
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              asChild
              className="gradient-cyber text-primary-foreground hover:glow-cyber-sm px-6 py-2 font-semibold"
            >
              <Link to="/contact">
                Get In Touch
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-border hover:border-primary">
              <Link to="/portfolio">View Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Skills Section */}
      <section ref={skillsRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-fluid-h2 text-foreground mb-8 text-center">Technical Skills</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
            {skills.map((skill) => (
              <GlassCard key={skill.name} hover className="skill-card cyber-accent-left py-4 pl-5">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                    <skill.icon className="text-primary h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground truncate text-sm font-medium">{skill.name}</p>
                    <p className="text-muted-foreground text-xs">{skill.category}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Experience Section */}
      <section ref={experienceRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="reveal mb-10 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-2">Experience</h2>
            <p className="text-muted-foreground">
              <span className="text-primary font-semibold">Software Engineer</span> at Re:Build Manufacturing
              <span className="text-muted-foreground/60 ml-2">Feb 2022 – Present</span>
            </p>
          </div>

          <GlassCard accent className="reveal">
            <div className="space-y-4">
              {experience.map((item, index) => (
                <div
                  key={item.title}
                  className={`flex gap-4 ${index !== experience.length - 1 ? 'border-border/50 border-b pb-4' : ''}`}
                >
                  <div className="bg-primary glow-cyber-sm mt-2 h-2 w-2 shrink-0 rounded-full" />
                  <div>
                    <h3 className="text-foreground font-semibold">{item.title}</h3>
                    <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Projects Section */}
      <section ref={projectsRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-fluid-h2 text-foreground mb-8 text-center">Selected Projects</h2>

          <div className="space-y-6">
            <GlassCard hover className="project-card">
              <div className="flex items-start gap-4">
                <div className="gradient-cyber flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <Cpu className="text-primary-foreground h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-foreground text-lg font-semibold">
                    Autonomous Lead-Gen Agent Fleet (~15 LLM agents)
                  </h3>
                  <p className="text-primary mt-1 font-mono text-xs">
                    AWS Bedrock · Claude Sonnet + Haiku · Lambda · DynamoDB · SES · Terraform
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm">
                    A system of ~15 LLM agents (plus a few supporting Lambdas) in three layers (sensors → synthesizers →
                    dispatchers) that discovers prospects, drafts grounded outreach behind a Haiku evidence-auditor
                    gate, and rewrites its own strategy weekly. Cutting the monthly Bedrock cap from $100 to $50 (in
                    config), plus a 72-hour re-draft dedup window and a fix to a self-contradicting drafter prompt,
                    roughly halved observed outreach cost. Runs unattended; deploys via GitHub Actions OIDC.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard hover className="project-card">
              <div className="flex items-start gap-4">
                <div className="gradient-cyber flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <Database className="text-primary-foreground h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-foreground text-lg font-semibold">
                    Operations Agent + Doctrine System (135 tools)
                  </h3>
                  <p className="text-primary mt-1 font-mono text-xs">
                    AWS Bedrock · Claude Sonnet 4.6 · Lambda · DynamoDB · Zod tool schemas
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm">
                    A Bedrock Claude agent that runs a whole operation — bookkeeping across three entities, real-estate
                    underwriting, CRM, scheduling. A doctrine system single-sources domain rules into both the agent
                    prompt and the Claude Code skills via <code className="font-mono text-xs">renderDoctrine()</code>,
                    so they never drift. 135 tools live in one registry — writes are staged as human-approved plan-ops.
                    An async self-invoke loop (180s) lets the agent run long tool-using turns past the 30s API Gateway
                    cap.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard hover className="project-card">
              <div className="flex items-start gap-4">
                <div className="gradient-cyber flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <Terminal className="text-primary-foreground h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-foreground text-lg font-semibold">AI Chatbot Infrastructure (Re:Build)</h3>
                  <p className="text-primary mt-1 font-mono text-xs">
                    Terraform · AWS ECS · Bedrock · LiteLLM · Open WebUI · CloudWatch
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Built multi-environment infrastructure for a RAG-backed AI chatbot from scratch. A build-vs-buy
                    decision (Open WebUI over a custom frontend) cut the team from 6 engineers to 2, and a LiteLLM +
                    batched-embeddings rebuild gave a 20x speedup on the embedding pipeline.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard hover className="project-card">
              <div className="flex items-start gap-4">
                <div className="gradient-cyber flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <Briefcase className="text-primary-foreground h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-foreground text-lg font-semibold">Manufacturing Scheduler</h3>
                  <p className="text-primary mt-1 font-mono text-xs">
                    .NET · Finite Scheduling Algorithms · Spreadsheet I/O
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Delivered a finite capacity scheduling application in 1 month through daily customer collaboration.
                    Simple design (spreadsheet in, spreadsheet out) outperformed a vendor solution that cost $30k/year.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Education Section */}
      <section ref={educationRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-fluid-h2 text-foreground mb-8 text-center">Education & Training</h2>

          <div className="grid gap-4 md:grid-cols-2">
            {education.map((edu) => (
              <GlassCard key={edu.title} hover className="edu-card cyber-accent-top pt-6">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                    <edu.icon className="text-primary h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-foreground text-sm font-semibold">{edu.title}</h3>
                    <p className="text-primary text-xs">{edu.institution}</p>
                    <p className="text-muted-foreground mt-1 text-xs">{edu.status}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>

          {/* Conference */}
          <div className="mt-8 text-center">
            <GlassCard className="inline-block">
              <div className="flex items-center gap-3">
                <Award className="text-primary h-5 w-5" />
                <div className="text-left">
                  <p className="text-foreground text-sm font-medium">AWS re:Invent 2025</p>
                  <p className="text-muted-foreground text-xs">Las Vegas, NV</p>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Contact CTA */}
      <section ref={contactRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-2xl">
          <GlassCard accent className="reveal py-10 text-center">
            <h2 className="text-fluid-h3 text-foreground mb-4">Let's Connect</h2>
            <p className="text-muted-foreground mb-6">Interested in working together? Let's talk.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild className="gradient-cyber text-primary-foreground hover:glow-cyber-sm">
                <a href="https://linkedin.com/in/garrettjohnlaw" target="_blank" rel="noopener noreferrer">
                  LinkedIn
                </a>
              </Button>
              <Button asChild variant="outline" className="border-border hover:border-primary">
                <a href="mailto:garrett@garrettjohnlaw.com">Email Me</a>
              </Button>
            </div>
          </GlassCard>
        </div>
      </section>
    </>
  )
}
